import { useEffect, useState, useRef, useCallback } from 'react';
import Editor, { type OnMount } from '@monaco-editor/react';
import type { editor } from 'monaco-editor';
import { Button } from '@/components/ui/button';
import { assetService, type Asset } from '@/services/asset-service';
import { toast } from 'sonner';
import { Save, Loader2, RefreshCw } from 'lucide-react';

type AssetJsonContentEditorProps = {
  assetId: string;
};

export const AssetJsonContentEditor = ({ assetId }: AssetJsonContentEditorProps) => {
  const [jsonString, setJsonString] = useState<string>('');
  const [pristineJsonString, setPristineJsonString] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);

  const fetchJsonContent = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Get the asset to check if it has a URI
      const asset = await assetService.getAsset(assetId);
      
      // Use the asset service method to properly get content
      const content = await assetService.getAssetContent(assetId);
      
      // Format the content for the editor
      let formattedJson: string;
      try {
        // If content is already an object, stringify it
        if (typeof content === 'object' && content !== null) {
          formattedJson = JSON.stringify(content, null, 2);
        } else {
          // If it's a string, try to parse and re-stringify for proper formatting
          const parsed = JSON.parse(content as string);
          formattedJson = JSON.stringify(parsed, null, 2);
        }
      } catch {
        // If it's not valid JSON, just use the raw content as a string
        formattedJson = typeof content === 'string' ? content : JSON.stringify(content, null, 2);
      }
      
      setJsonString(formattedJson);
      setPristineJsonString(formattedJson);
    } catch (err) {
      console.error('Error fetching JSON content:', err);
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [assetId]);

  useEffect(() => {
    if (assetId) {
      fetchJsonContent();
    }
  }, [assetId, fetchJsonContent]);

  const handleSave = async () => {
    setIsSaving(true);

    try {
      // Validate JSON format
      JSON.parse(jsonString);
    } catch (parseError) {
      toast.error("Invalid JSON format. Please correct the errors before saving.");
      setIsSaving(false);
      return;
    }
    
    try {
      // Get the asset to check if it has a URI that needs to be updated
      const asset = await assetService.getAsset(assetId);
      
      if (asset.data && typeof asset.data === 'object' && 'uri' in asset.data) {
        // This is a file-based asset, need to upload new content to R2 and update the URI
        const uri = asset.data.uri as string;
        
        // Create a Blob with the updated JSON content
        const updatedJsonBlob = new Blob([jsonString], { type: 'application/json' });
        
        // Create a File from the Blob with the appropriate name
        // Extract original filename from URI or create a new one
        let fileName = `updated-${assetId}.json`;
        if (uri && typeof uri === 'string') {
          const pathParts = uri.split('/');
          const originalFileName = pathParts[pathParts.length - 1];
          if (originalFileName && originalFileName.endsWith('.json')) {
            fileName = originalFileName;
          }
        }
        
        const updatedJsonFile = new File([updatedJsonBlob], fileName, { type: 'application/json' });
        
        // Update the asset with the new file content
        // This will create a new version with the updated content
        await assetService.updateAssetFileContent(assetId, updatedJsonFile, asset.name, asset.type);
      } else {
        // This is a data-based asset, update the asset data directly (existing behavior)
        const jsonData = JSON.parse(jsonString);
        await assetService.updateAsset(assetId, { data: jsonData });
      }
      
      // On successful save, update the pristine state to the new content
      setPristineJsonString(jsonString);
      toast.success("JSON content saved successfully!");
    } catch (err) {
      console.error('Error saving JSON content:', err);
      toast.error("Failed to save JSON content. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditorDidMount: OnMount = (editorInstance) => {
    editorRef.current = editorInstance;
    setTimeout(() => {
      editorInstance.getAction('editor.action.formatDocument')?.run();
    }, 200);
  };
  
  // Determine if the content has been modified
  const isModified = jsonString !== pristineJsonString;

  if (isLoading) return <div className="flex h-full w-full items-center justify-center">Loading Editor...</div>;
  if (error) return <div className="flex h-full w-full items-center justify-center text-destructive">Error: {error}</div>;

  return (
    <div className="flex flex-col h-full w-full gap-2">
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={fetchJsonContent} disabled={isLoading}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
        <Button onClick={handleSave} disabled={isSaving || !isModified}>
          {isSaving ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
      <div className="flex-1 rounded-md overflow-hidden border">
        <Editor
          height="100%"
          language="json"
          theme="vs-dark"
          value={jsonString}
          onMount={handleEditorDidMount}
          onChange={(value) => setJsonString(value || '')}
          options={{
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            automaticLayout: true,
          }}
        />
      </div>
    </div>
  );
};